// Assorted presentational helpers: avatars, prose, time picker.

import { Fragment, useState, type ReactNode } from 'react';
import type { Person } from '../domain/types.ts';
import { MINUTE, formatTime } from '../domain/time.ts';

export function Avatar({ person, size = 36, ring }: { person?: Person; size?: number; ring?: boolean }) {
  const initial = person?.name?.[0]?.toUpperCase() ?? '?';
  return (
    <span
      className={`avatar${ring ? ' avatar-ring' : ''}`}
      style={{ width: size, height: size, fontSize: size * 0.44, background: person?.color ?? 'var(--muted)' }}
      aria-label={person?.name}
      title={person?.name}
    >
      {initial}
    </span>
  );
}

/** Renders the tiny markdown subset documented in content/types.ts. */
export function Prose({ text }: { text: string }) {
  const blocks = text.trim().split(/\n{2,}/);
  return (
    <div className="prose">
      {blocks.map((block, i) => {
        const lines = block.split('\n');
        if (lines.every((l) => /^\s*[-*] /.test(l)))
          return (
            <ul key={i}>
              {lines.map((l, j) => (
                <li key={j}>{inline(l.replace(/^\s*[-*] /, ''))}</li>
              ))}
            </ul>
          );
        if (lines.every((l) => /^\s*\d+\. /.test(l)))
          return (
            <ol key={i}>
              {lines.map((l, j) => (
                <li key={j}>{inline(l.replace(/^\s*\d+\. /, ''))}</li>
              ))}
            </ol>
          );
        // Mixed paragraph + list: render lines individually.
        if (lines.some((l) => /^\s*([-*] |\d+\. )/.test(l))) {
          const out: ReactNode[] = [];
          let list: string[] = [];
          const flush = () => {
            if (list.length) out.push(<ul key={`l${out.length}`}>{list.map((l, j) => <li key={j}>{inline(l)}</li>)}</ul>);
            list = [];
          };
          lines.forEach((l) => {
            if (/^\s*([-*] |\d+\. )/.test(l)) list.push(l.replace(/^\s*([-*] |\d+\. )/, ''));
            else {
              flush();
              out.push(<p key={`p${out.length}`}>{inline(l)}</p>);
            }
          });
          flush();
          return <Fragment key={i}>{out}</Fragment>;
        }
        if (/^#{1,4} /.test(block) && lines.length === 1) return <h4 key={i} className="prose-h">{inline(block.replace(/^#{1,4} /, ''))}</h4>;
        return <p key={i}>{inline(block.replace(/^#{1,4} /gm, '').replace(/\n/g, ' '))}</p>;
      })}
    </div>
  );
}

function inline(text: string): ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => (p.startsWith('**') && p.endsWith('**') ? <strong key={i}>{p.slice(2, -2)}</strong> : <Fragment key={i}>{p}</Fragment>));
}

function toLocalInput(t: number): string {
  const d = new Date(t);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** "When did it happen?" — quick offsets plus an exact picker. */
export function WhenPicker({ value, onChange, label = 'When' }: { value: number; onChange: (t: number) => void; label?: string }) {
  const [base] = useState(() => Date.now());
  const offsets = [0, 5, 15, 30, 60];
  const offset = Math.round((base - value) / MINUTE);
  return (
    <div className="when">
      <span className="field-label">{label}</span>
      <div className="chips">
        {offsets.map((m) => (
          <button key={m} type="button" className={`chip${offset === m ? ' on' : ''}`} onClick={() => onChange(base - m * MINUTE)}>
            {m === 0 ? 'Now' : m < 60 ? `${m} min ago` : '1 hr ago'}
          </button>
        ))}
      </div>
      <input
        type="datetime-local"
        className="input"
        value={toLocalInput(value)}
        max={toLocalInput(base + 5 * MINUTE)}
        onChange={(e) => {
          const t = new Date(e.target.value).getTime();
          if (!Number.isNaN(t)) onChange(t);
        }}
        aria-label={`${label} (exact time)`}
      />
      <span className="field-hint">{formatTime(value)}</span>
    </div>
  );
}

export function toLocalDateTimeInput(t: number) {
  return toLocalInput(t);
}
